import { BUTTON_MODES, BUTTON_VARIANTS, ButtonMode, ButtonVariant, CLOSE_BUTTON_VARIANTS } from './buttonVariants';
import { PX_TO_REM, PxToRem, SIZES } from '../../fundamentals/sizes/sizes';
import { ButtonSize } from './Button';
import merge from 'lodash.merge';
import { IColoursCollection, MapColourStates, StateColour } from '../../fundamentals/colours/colourInterfaces';
import { DEFAULT_DISABLED_STATE, generateColourState } from '../../fundamentals/colours/variants';

export interface IButtonColours {
  buttonColours: Record<ButtonVariant, Record<ButtonMode, MapColourStates>>;
}

const BASE_BUTTON_BORDER_STYLE = `1px solid`;

const generateLinkColour = (colour: string): MapColourStates => ({
  text: colour,
  textDecoration: 'underline',
  hover: {
    textDecoration: 'none'
  },
  active: {
    textDecoration: 'underline'
  },
  disabled: DEFAULT_DISABLED_STATE
});

const generateMinimalColours = (colours: StateColour, mainColourOverride?: string): MapColourStates =>
  merge({}, generateColourState(colours), {
    background: 'transparent',
    text: mainColourOverride ?? colours.colour,
    hover: {
      text: colours.text
    },
    active: {
      text: colours.text
    }
  });

const generateCloseMinimalColours = (colours: StateColour, minimalColours: StateColour): MapColourStates =>
  merge({}, generateColourState(colours), {
    background: minimalColours.colour,
    text: minimalColours.text,
    hover: {
      background: minimalColours.hover
    },
    active: {
      background: minimalColours.active
    }
  });

const generateOutlineColours = (colours: StateColour, mainColourOverride?: string): MapColourStates =>
  merge({}, generateMinimalColours(colours, mainColourOverride), {
    border: `${BASE_BUTTON_BORDER_STYLE} ${mainColourOverride ?? colours.colour}`,
    hover: {
      border: `${BASE_BUTTON_BORDER_STYLE} transparent`
    },
    active: {
      border: `${BASE_BUTTON_BORDER_STYLE} transparent`
    }
  });

const generateModesColours = (colours: StateColour, mainColourOverride?: string) => ({
  [BUTTON_MODES.BOLD]: generateColourState(colours),
  [BUTTON_MODES.OUTLINE]: generateOutlineColours(colours, mainColourOverride),
  [BUTTON_MODES.MINIMAL]: generateMinimalColours(colours, mainColourOverride)
});

const generateLinkModesColours = (colour: string) => {
  const computedColour = generateLinkColour(colour);
  return {
    [BUTTON_MODES.BOLD]: computedColour,
    [BUTTON_MODES.OUTLINE]: computedColour,
    [BUTTON_MODES.MINIMAL]: computedColour
  };
};

const generateModesColoursCloseButton = (colours: StateColour, minimalColours: StateColour) => ({
  [BUTTON_MODES.BOLD]: generateColourState(colours),
  [BUTTON_MODES.OUTLINE]: generateCloseMinimalColours(colours, minimalColours), // no outline for Close button
  [BUTTON_MODES.MINIMAL]: generateCloseMinimalColours(colours, minimalColours)
});

export const buttonThemeConfig = ({
  base,
  accent,
  primary,
  secondary,
  secondaryInverted,
  text,
  status,
  opaque,
  closeButtonOnPrimary,
  eoraBrandColours
}: IColoursCollection): IButtonColours => ({
  buttonColours: {
    [BUTTON_VARIANTS.ACCENT]: generateModesColours(accent),
    [BUTTON_VARIANTS.PRIMARY]: generateModesColours(primary),
    [BUTTON_VARIANTS.SECONDARY]: generateModesColours(secondary, secondary.text),
    [BUTTON_VARIANTS.SECONDARY_INVERTED]: generateModesColours(secondaryInverted, secondaryInverted.text),
    [BUTTON_VARIANTS.ERROR]: generateModesColours(status.error),
    [BUTTON_VARIANTS.SUCCESS]: generateModesColours(status.success),
    [BUTTON_VARIANTS.WARNING]: generateModesColours(status.warning),
    [BUTTON_VARIANTS.WHITE]: generateModesColours(base.white),
    [BUTTON_VARIANTS.WHITE_NO_SWITCH]: generateModesColours(base.white),
    [BUTTON_VARIANTS.BLACK]: generateModesColours(base.black),
    [BUTTON_VARIANTS.BLACK_NO_SWITCH]: generateModesColours(base.black),
    [BUTTON_VARIANTS.OPAQUE_WHITE]: generateModesColours(opaque.white, base.white.colour),
    [BUTTON_VARIANTS.OPAQUE_BLACK]: generateModesColours(opaque.black, base.black.colour),
    [BUTTON_VARIANTS.LINK_ACCENT]: generateLinkModesColours(accent.link!),
    [BUTTON_VARIANTS.LINK_PRIMARY]: generateLinkModesColours(
      eoraBrandColours.BRAND_WL_COLORS.WL_COLOR_BUTTON_TEXT_LINK_PRIMARY || eoraBrandColours.MODE.BRAND_WL_COLORS?.WL_COLOR_BUTTON_TEXT_LINK_PRIMARY!
    ),
    [BUTTON_VARIANTS.LINK_WHITE]: generateLinkModesColours(base.white.colour),
    [BUTTON_VARIANTS.LINK_DARK]: generateLinkModesColours(text.default),

    [BUTTON_VARIANTS.LINK]: generateLinkModesColours(
      eoraBrandColours.BRAND_WL_COLORS.WL_COLOR_BUTTON_TEXT_LINK_PRIMARY || eoraBrandColours.MODE.BRAND_WL_COLORS?.WL_COLOR_BUTTON_TEXT_LINK_PRIMARY!
    ),
    [BUTTON_VARIANTS.HERO]: generateModesColours(accent),

    [CLOSE_BUTTON_VARIANTS.WHITE50]: generateModesColoursCloseButton(opaque.white50, opaque.white50Minimal),
    [CLOSE_BUTTON_VARIANTS.BLACK50]: generateModesColoursCloseButton(opaque.black50, opaque.black50Minimal),
    [CLOSE_BUTTON_VARIANTS.WHITE50_NO_SWITCH]: generateModesColoursCloseButton(opaque.white50NoSwitch, opaque.white50NoSwitchMinimal),
    [CLOSE_BUTTON_VARIANTS.BLACK50_NO_SWITCH]: generateModesColoursCloseButton(opaque.black50NoSwitch, opaque.black50NoSwitchMinimal),
    [CLOSE_BUTTON_VARIANTS.BRAND_AWARE]: generateModesColoursCloseButton(closeButtonOnPrimary, closeButtonOnPrimary) // only minimal style exists, no bold for this variant
  }
});

// passed to theme settings
/**
 * @deprecated Use standardBorderRadiusSizes or buttonTabBadgeRadiusSizes
 */
export const buttonBorderRadius: Record<ButtonSize, PxToRem> = {
  [SIZES.X_SMALL]: PX_TO_REM[12],
  [SIZES.SMALL]: PX_TO_REM[16],
  [SIZES.MEDIUM]: PX_TO_REM[20],
  [SIZES.LARGE]: PX_TO_REM[24]
};
